import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Shield, Twitter } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const DiscordGlyph: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
  </svg>
);

const Footer: React.FC = () => {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://images.dmca.com/Badges/DMCABadgeHelper.min.js';
    script.async = true;
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[src="https://images.dmca.com/Badges/DMCABadgeHelper.min.js"]');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: t('footer.quickLinks.home'), to: '/' },
    { label: t('footer.quickLinks.about'), to: '/about' },
    { label: t('footer.quickLinks.legal'), to: '/legal' },
    { label: t('footer.quickLinks.privacy'), to: '/privacy-policy' },
    { label: t('footer.quickLinks.contact'), to: '/contact' }
  ];

  const serviceLinks = [
    { label: t('footer.services.reverseProxy'), to: '/services/reverse-proxy' },
    { label: t('footer.services.vps'), to: '/services/vps' },
    { label: t('footer.services.cdn'), to: '/services/cdn-caching' },
    { label: t('footer.services.panel'), to: '/services/game-panel' }
  ];

  const socials = [
    {
      label: 'Discord',
      href: 'https://discord.gg/fivemshield',
      icon: <DiscordGlyph className="h-4 w-4" />
    },
    {
      label: 'Telegram',
      href: 'https://t.me/fivemshieldnetwork',
      icon: <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" /></svg>
    },
    {
      label: 'Espace Clients',
      href: 'https://manager.fivemshield.net',
      icon: <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
    },
    {
      label: 'Tickets',
      href: 'https://manager.fivemshield.net',
      icon: <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 5v2" /><path d="M15 11v2" /><path d="M15 17v2" /><path d="M5 5a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5z" /><path d="M5 12a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H5z" /></svg>
    },
    {
      label: 'X / Twitter',
      href: 'https://x.com/FIVEMSHIELD',
      icon: <Twitter className="h-4 w-4" />
    }
  ];

  return (
    <footer className="relative overflow-hidden bg-black border-t border-white/5 w-full">
      {/* Headline + CTA */}
      <div className="relative z-20 grid w-full grid-cols-1 gap-12 px-6 py-20 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-7">
          <h2 className="mb-10 font-sans text-5xl font-medium leading-[0.9] tracking-tighter text-white lg:text-[5.5rem]">
            {t('footerV2.headline1')}
            <br />
            <span className="text-neutral-700">{t('footerV2.headline2')}</span>
            <br />
            {t('footerV2.headline3')}
          </h2>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            {[...quickLinks.filter((link) => link.to !== '/legal' && link.to !== '/privacy-policy'), ...serviceLinks].map((link) => (
              <Link key={link.to} to={link.to} className="text-sm text-neutral-500 transition-colors hover:text-white">
                {link.label}
              </Link>
            ))}
            <a href="mailto:contact@fivemshield.net" className="text-sm text-neutral-500 transition-colors hover:text-white">
              contact@fivemshield.net
            </a>
            <div className="flex items-center gap-1">
              {socials.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 transition-colors hover:bg-white/5 hover:text-white"
                  aria-label={social.label}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center lg:col-span-5 lg:justify-end">
          <a
            href="https://manager.fivemshield.net"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative w-full max-w-sm overflow-hidden bg-gradient-to-br from-primary to-primary-light p-8 transition-transform duration-500 hover:-translate-y-2"
          >
            <Shield className="absolute -right-4 -top-4 h-32 w-32 rotate-12 text-black/10" strokeWidth={1.5} />
            <div className="relative z-10">
              <h3 className="mb-4 text-2xl font-bold tracking-tight text-white">
                {t('footerV2.ctaCard.title')}
              </h3>
              <p className="mb-8 text-sm font-medium leading-relaxed text-white/70">
                {t('footerV2.ctaCard.description')}
              </p>
              <div className="flex items-center justify-between border-t border-white/15 pt-4">
                <span className="text-xs font-bold uppercase tracking-widest text-white">
                  {t('footerV2.ctaCard.badge')}
                </span>
                <ArrowUpRight className="h-5 w-5 text-white" />
              </div>
            </div>
          </a>
        </div>
      </div>

      {/* Watermark */}
      <div className="relative w-full overflow-hidden pointer-events-none select-none pt-4">
        <h1 className="text-[16vw] leading-none font-bold text-white/5 text-center -mb-8 tracking-tighter">
          {t('footerV2.watermark')}
        </h1>
        <div className="absolute bottom-0 left-0 h-24 w-full bg-gradient-to-t from-black to-transparent" />
      </div>

      <div className="relative z-20 px-6 py-6">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 text-sm text-gray-500 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <a
              href="//www.dmca.com/Protection/Status.aspx?ID=04922092-a6ad-4c0b-b07e-531bf3a47bb9"
              title="DMCA.com Protection Status"
              className="dmca-badge inline-flex items-center"
            >
              <img
                src="https://images.dmca.com/Badges/dmca-badge-w200-2x1-03.png?ID=04922092-a6ad-4c0b-b07e-531bf3a47bb9"
                alt="DMCA.com Protection Status"
                className="h-8"
              />
            </a>
            <span>&copy; {currentYear} FIVEMSHIELD · {t('footer.copyright')}</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/legal" className="text-gray-400 transition hover:text-white">
              {t('footer.quickLinks.legal')}
            </Link>
            <span className="text-gray-700">·</span>
            <Link to="/privacy-policy" className="text-gray-400 transition hover:text-white">
              {t('footer.quickLinks.privacy')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
